// ─────────────────────────────────────────────────────────────────
// Glass-i — HRMS Employee Transfers page (Stage HRMS-3).
// Create a DRAFT transfer (shows the employee's current posting, pick
// the new one), approve it (applies to the employee), or cancel a
// draft. Approved transfers are locked. RBAC server-side.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button, DatePicker, Descriptions, Form, Input, Modal, Popconfirm, Select,
  Space, Table, Tag, Tooltip, message
} from 'antd';
import { PlusOutlined, ReloadOutlined, SwapOutlined, CheckOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { hrTransfersApi } from './hrTransfersApi';
import { hrEmployeesApi } from '../employees/hrEmployeesApi';
import { hrDepartmentsApi } from '../departments/hrDepartmentsApi';
import { hrDesignationsApi } from '../designations/hrDesignationsApi';
import { hrBranchesApi } from '../branches/hrBranchesApi';
import {
  TRANSFER_STATUS_OPTIONS, transferStatusColor, isDraft, buildTransferPayload, describeChange
} from './transfersHelpers';
import '../hrms.css';

const PAGE_SIZE = 20;

export default function EmployeeTransfersPage() {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [empById, setEmpById] = useState({});
  const [branches, setBranches] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentEmp, setCurrentEmp] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await hrTransfersApi.list({
        status: statusFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setRows((out && out.rows) || []);
      setTotal((out && out.total) || 0);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load transfers.');
    } finally { setLoading(false); }
  }, [statusFilter, page]);
  useEffect(() => { load(); }, [load]);

  const loadLookups = useCallback(async () => {
    try {
      const [emp, b, d, g] = await Promise.all([
        hrEmployeesApi.list({ limit: 1000 }), hrBranchesApi.lookups(),
        hrDepartmentsApi.lookups(), hrDesignationsApi.lookups()
      ]);
      const empRows = (emp && emp.rows) || [];
      setEmployees(empRows.map((x) => ({ value: x.id, label: `${x.displayName || x.employeeName} (${x.employeeCode})` })));
      setEmpById(Object.fromEntries(empRows.map((x) => [x.id, x])));
      setBranches(((b && b.rows) || []).map((x) => ({ value: x.id, label: x.branchName })));
      setDepts(((d && d.rows) || []).map((x) => ({ value: x.id, label: x.departmentName })));
      setDesigs(((g && g.rows) || []).map((x) => ({ value: x.id, label: x.designationName })));
    } catch (_e) { /* soft-fail */ }
  }, []);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  const managerOpts = useMemo(() => employees, [employees]);

  const openCreate = useCallback((preEmployeeId) => {
    form.resetFields();
    form.setFieldsValue({ effectiveDate: dayjs(), employeeId: preEmployeeId || undefined });
    setCurrentEmp(preEmployeeId ? empById[preEmployeeId] || null : null);
    if (preEmployeeId && empById[preEmployeeId]) {
      const e = empById[preEmployeeId];
      form.setFieldsValue({ toBranchId: e.branchId, toDepartmentId: e.departmentId, toDesignationId: e.designationId, toManagerId: e.reportsToEmployeeId });
    }
    setModalOpen(true);
  }, [form, empById]);

  // Support "Create Transfer" deep-link from the Employees page.
  useEffect(() => {
    const pre = location.state && location.state.employeeId;
    if (pre && Object.keys(empById).length) { openCreate(pre); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, empById]);

  const onEmployeeChange = (id) => {
    const e = empById[id] || null;
    setCurrentEmp(e);
    if (e) form.setFieldsValue({ toBranchId: e.branchId, toDepartmentId: e.departmentId, toDesignationId: e.designationId, toManagerId: e.reportsToEmployeeId });
  };

  const submit = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { message.error('Please fix the highlighted fields.'); return; }
    setSaving(true);
    try {
      await hrTransfersApi.create(buildTransferPayload(values));
      message.success('Transfer draft created.');
      setModalOpen(false); load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to create transfer.');
    } finally { setSaving(false); }
  };

  const approve = async (rec) => {
    try { await hrTransfersApi.approve(rec.id); message.success('Transfer approved and applied to the employee.'); load(); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to approve transfer.'); }
  };
  const cancel = async (rec) => {
    try { await hrTransfersApi.cancel(rec.id); message.success('Transfer cancelled.'); load(); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to cancel transfer.'); }
  };

  const columns = [
    { title: 'Employee', dataIndex: 'employeeName', width: 190,
      render: (v, r) => `${v || '—'}${r.employeeCode ? ` (${r.employeeCode})` : ''}` },
    { title: 'Type', dataIndex: 'transferType', width: 110, render: (v) => <Tag>{v}</Tag> },
    { title: 'Change', key: 'change', render: (_v, r) => <span className="hr-mono">{describeChange(r)}</span> },
    { title: 'Effective', dataIndex: 'effectiveDate', width: 120, render: (v) => v ? dayjs(v).format('DD-MM-YYYY') : '—' },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={transferStatusColor(v)}>{v}</Tag> },
    { title: 'Actions', key: 'actions', width: 150, align: 'center', fixed: 'right',
      render: (_v, rec) => isDraft(rec) ? (
        <Space size={4}>
          <Popconfirm title="Approve this transfer? It updates the employee." okText="Approve" onConfirm={() => approve(rec)}>
            <Tooltip title="Approve"><Button size="small" type="text" icon={<CheckOutlined />} /></Tooltip>
          </Popconfirm>
          <Popconfirm title="Cancel this draft transfer?" okText="Cancel draft" okButtonProps={{ danger: true }} onConfirm={() => cancel(rec)}>
            <Tooltip title="Cancel draft"><Button size="small" type="text" danger icon={<StopOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ) : <span style={{ color: '#cbd5e1' }}>—</span> }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><SwapOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Employee Transfers</span>
            <span className="hr-headstrip__subtitle">Move an employee's branch, department, designation or manager — draft then approve.</span>
          </div>
        </div>
        <Space size={6}><span className="hr-stat-chip"><strong>{total}</strong> total</span></Space>
      </div>

      <div className="hr-toolbar">
        <Select size="small" allowClear placeholder="Status" value={statusFilter || undefined} style={{ width: 150 }}
          options={TRANSFER_STATUS_OPTIONS} onChange={(v) => { setPage(1); setStatusFilter(v || null); }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>New Transfer</Button>
      </div>

      <div className="hr-card">
        <Table className="hr-grid" size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns}
          scroll={{ x: 'max-content' }} sticky
          pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, onChange: setPage, size: 'small' }}
          locale={{ emptyText: 'No transfers yet. Click New Transfer to start.' }} />
      </div>

      <Modal open={modalOpen} title="New Employee Transfer" onCancel={() => setModalOpen(false)}
        onOk={submit} confirmLoading={saving} okText="Save Draft" width={720} destroyOnClose>
        <Form form={form} layout="vertical" size="small" style={{ marginTop: 4 }}>
          <Form.Item name="employeeId" label="Employee" rules={[{ required: true, message: 'Select an employee.' }]}>
            <Select showSearch optionFilterProp="label" options={employees} placeholder="Select employee…" onChange={onEmployeeChange} />
          </Form.Item>

          {currentEmp ? (
            <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}
              items={[
                { key: 'b', label: 'Current Branch', children: currentEmp.branchName || '—' },
                { key: 'd', label: 'Current Department', children: currentEmp.departmentName || '—' },
                { key: 'g', label: 'Current Designation', children: currentEmp.designationName || '—' },
                { key: 'm', label: 'Current Manager', children: currentEmp.reportsToName || '—' }
              ]} />
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="toBranchId" label="New Branch"><Select allowClear showSearch optionFilterProp="label" options={branches} /></Form.Item>
            <Form.Item name="toDepartmentId" label="New Department"><Select allowClear showSearch optionFilterProp="label" options={depts} /></Form.Item>
            <Form.Item name="toDesignationId" label="New Designation"><Select allowClear showSearch optionFilterProp="label" options={desigs} /></Form.Item>
            <Form.Item name="toManagerId" label="New Manager"><Select allowClear showSearch optionFilterProp="label"
              options={managerOpts.filter((o) => !currentEmp || o.value !== currentEmp.id)} /></Form.Item>
          </div>
          <Form.Item name="effectiveDate" label="Effective Date" rules={[{ required: true, message: 'Effective date is required.' }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item name="reason" label="Reason"><Input.TextArea rows={2} maxLength={2000} /></Form.Item>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} maxLength={2000} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
